// Copyright (C) 2024 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React, { useState } from 'react';
import { useHistory } from 'react-router';
import { useSelector } from 'react-redux';
import { Row, Col } from 'antd/lib/grid';
import Button from 'antd/lib/button';
import Tooltip from 'antd/lib/tooltip';
import { ArrowLeftOutlined, PlusOutlined, DownloadOutlined } from '@ant-design/icons';

import { CombinedState, LabelingRunData } from 'reducers';
import dimensions from 'utils/dimensions';
import AddTasksModal from './add-tasks-modal';
import LabelCountsDownloadModal from '../analytics-page/label-counts-download-modal';

interface Props {
    run: LabelingRunData;
    runId: number;
}

export default function TopBarComponent({ run, runId }: Props): JSX.Element {
    const history = useHistory();
    const [modalVisible, setModalVisible] = useState(false);
    const [downloadVisible, setDownloadVisible] = useState(false);
    const user = useSelector((state: CombinedState) => state.auth.user);
    const canManage = !!user && (user.isSuperuser || user.isStaff || user.groups.includes('admin'));

    return (
        <Row className='cvat-run-page-top-bar' justify='center' align='middle'>
            <Col {...dimensions}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Button
                            type='link'
                            icon={<ArrowLeftOutlined />}
                            onClick={() => history.push('/labeling-runs')}
                        />
                        <span style={{ fontSize: 20, fontWeight: 600 }}>{run.name}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <Button
                            icon={<DownloadOutlined />}
                            onClick={() => setDownloadVisible(true)}
                        >
                            Download Label Counts
                        </Button>
                        <Tooltip title={canManage ? '' : 'You do not have permission to add tasks'}>
                            <span>
                                <Button
                                    type='primary'
                                    icon={<PlusOutlined />}
                                    disabled={!canManage}
                                    onClick={() => setModalVisible(true)}
                                >
                                    Add Tasks
                                </Button>
                            </span>
                        </Tooltip>
                    </div>
                </div>
            </Col>
            <AddTasksModal runId={runId} visible={modalVisible} onClose={() => setModalVisible(false)} />
            <LabelCountsDownloadModal
                runId={runId}
                visible={downloadVisible}
                onClose={() => setDownloadVisible(false)}
            />
        </Row>
    );
}
